import type { JSX } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CreateJobPositionFormData } from '../../schemas/jobPosition.schemas';
import type { ExperienceLevel } from '../../types/jobPosition.types';
import type { FormSectionProps } from './types';
import { ExperienceLevelCheckboxes } from './ExperienceLevelCheckboxes';

type SelectFieldProps = Pick<FormSectionProps, 'watch' | 'setValue'> & {
  error?: { message?: string };
};

function EmploymentTypeField({ watch, setValue, error }: SelectFieldProps): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Label htmlFor="employmentType">Employment Type *</Label>
      <Select
        value={watch('employmentType')}
        onValueChange={(value) =>
          setValue('employmentType', value as CreateJobPositionFormData['employmentType'])
        }
      >
        <SelectTrigger>
          <SelectValue placeholder="Select employment type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="FULL_TIME">Full Time</SelectItem>
          <SelectItem value="PART_TIME">Part Time</SelectItem>
          <SelectItem value="CONTRACT">Contract</SelectItem>
          <SelectItem value="FREELANCE">Freelance</SelectItem>
          <SelectItem value="INTERNSHIP">Internship</SelectItem>
        </SelectContent>
      </Select>
      {error && <p className="text-sm text-red-500">{error.message}</p>}
    </div>
  );
}

function RemotePolicyField({ watch, setValue, error }: SelectFieldProps): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Label htmlFor="remotePolicy">Remote Policy *</Label>
      <Select
        value={watch('remotePolicy')}
        onValueChange={(value) =>
          setValue('remotePolicy', value as CreateJobPositionFormData['remotePolicy'])
        }
      >
        <SelectTrigger>
          <SelectValue placeholder="Select remote policy" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="REMOTE">Remote</SelectItem>
          <SelectItem value="HYBRID">Hybrid</SelectItem>
          <SelectItem value="ON_SITE">On Site</SelectItem>
        </SelectContent>
      </Select>
      {error && <p className="text-sm text-red-500">{error.message}</p>}
    </div>
  );
}

export function EmploymentSection({ errors, setValue, watch }: FormSectionProps): JSX.Element {
  const experienceLevels = watch('experienceLevels') || [];

  const toggleExperienceLevel = (level: ExperienceLevel): void => {
    const current = experienceLevels;
    if (current.includes(level)) {
      setValue(
        'experienceLevels',
        current.filter((l) => l !== level)
      );
    } else {
      setValue('experienceLevels', [...current, level]);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Employment Details</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <EmploymentTypeField watch={watch} setValue={setValue} error={errors.employmentType} />
        <RemotePolicyField watch={watch} setValue={setValue} error={errors.remotePolicy} />
      </div>
      <div className="space-y-2">
        <Label>Experience Levels *</Label>
        <ExperienceLevelCheckboxes
          experienceLevels={experienceLevels}
          onToggle={toggleExperienceLevel}
        />
        {errors.experienceLevels && (
          <p className="text-sm text-red-500">{errors.experienceLevels.message}</p>
        )}
      </div>
    </div>
  );
}
